// import useClickTimeline from "../../../hooks/useClickTimeline";
// import useNormalizedTimeline from "../../../hooks/useNormalizedTimeline";
// import React, { useMemo, useRef, useState } from 'react';
// import { ChevronDown, Clock } from 'lucide-react';
// import { LineChart, Line, XAxis, YAxis, CartesianGrid, ResponsiveContainer, Tooltip } from 'recharts';
// import { useClickOutside } from "../../../hooks/ClickoutsideHook";
// import NoDataFound from "../dataDisplay/NoDataFound";
// import DatePicker from 'react-datepicker';
// import "react-datepicker/dist/react-datepicker.css";

// /**
//  * Displays tap activity data over time (hourly, daily, or monthly) in a line chart.
//  *
//  * @param {object} props - The component props.
//  * @param {string} props.eventId - The unique ID of the event to fetch analytics for.
//  * @param {string} props.token - The user authentication token (passed to the hook for dependency).
//  * @returns {React.ReactElement} The Engagement Timeline chart component.
//  */
// const EngagementChart = ({ eventId, token }) => {
//     const { data: raw, loading, error } = useClickTimeline({ eventId, token });
//     const { hourly, daily, monthly, minutes } = useNormalizedTimeline(raw);

//     const [timeRange, setTimeRange] = useState('daily');
//     const [startDate, setStartDate] = useState(new Date());
//     const [endDate, setEndDate] = useState(new Date());
//     const [isDateFilterEnabled, setIsDateFilterEnabled] = useState(false);

//     const data = useMemo(() => {
//         switch (timeRange) {
//             case 'hourly': return hourly;
//             case 'monthly': return monthly;
//             case 'minutes': return minutes;
//             default: return daily;
//         }
//     }, [timeRange, hourly, daily, monthly, minutes]);

//     // Filter the data if the date filter is enabled
//     const filteredData = useMemo(() => {
//         if (!isDateFilterEnabled) return data;
//         return data.filter(item => {
//             const itemDate = new Date(item.date);
//             return itemDate >= startDate && itemDate <= endDate;
//         });
//     }, [data, isDateFilterEnabled, startDate, endDate]);

//     const isDataEmpty = !loading && !error && filteredData.length === 0;

//     const TimeRangeSelector = () => {
//         const [isOpen, setIsOpen] = useState(false);
//         const ref = useRef(null);
//         useClickOutside(ref, () => setIsOpen(false));

//         const options = [
//             { value: 'minutes', label: 'Minutes Data' },
//             { value: 'hourly', label: 'Hourly Data' },
//             { value: 'daily', label: 'Daily Data' },
//             { value: 'monthly', label: 'Monthly Data' },
//         ];
//         const currentOption = options.find((opt) => opt.value === timeRange);

//         return (
//             <div className="relative w-full sm:w-auto" ref={ref}>
//                 <button
//                     onClick={() => setIsOpen(!isOpen)}
//                     className="w-full sm:w-auto bg-[var(--analytics-card-color)] text-black px-3 py-2 rounded-lg flex items-center justify-between sm:justify-center space-x-2 transition-colors border border-gray-600 cursor-pointer"
//                 >
//                     <span className="text-sm sm:text-xs md:text-sm font-medium">{currentOption?.label}</span>
//                     <ChevronDown className={`w-4 h-4 transition-transform ${isOpen ? 'rotate-180' : ''}`} />
//                 </button>
//                 {isOpen && (
//                     <div className="absolute top-full mt-2 right-0 left-0 sm:left-auto bg-[var(--analytics-card-color)] border border-[var(--border-color)] rounded-lg shadow-lg z-10 min-w-[160px]">
//                         {options.map((option) => (
//                             <button
//                                 key={option.value}
//                                 onClick={() => { setTimeRange(option.value); setIsOpen(false); }}
//                                 className={`w-full text-left px-4 py-2 text-sm hover:bg-gray-300 transition-colors first:rounded-t-lg last:rounded-b-lg ${timeRange === option.value ? 'bg-gray-300 text-orange-400' : 'text-black'}`}
//                             >
//                                 {option.label}
//                             </button>
//                         ))}
//                     </div>
//                 )}
//             </div>
//         );
//     };

//     const CustomTooltip = ({ active, payload, label }) => {
//         if (!active || !payload || !payload.length) return null;
//         const getTooltipLabel = () =>
//             timeRange === 'hourly' ? 'Time' :
//                 timeRange === 'monthly' ? 'Month' :
//                     'Date';
//         const getUnit = () =>
//             timeRange === 'hourly' ? 'taps/hour' :
//                 timeRange === 'monthly' ? 'taps/month' :
//                     timeRange === 'daily' ? 'taps/day' : 'taps/Minute';

//         return (
//             <div className="bg-[var(--analytics-card-color)] border border-[var(--border-color)] rounded-lg p-3 shadow-lg">
//                 <p className="text-black text-xs sm:text-sm font-medium">{`${getTooltipLabel()}: ${label}`}</p>
//                 <p className="text-orange-400 text-xs sm:text-sm">{`Engagement: ${payload[0].value} ${getUnit()}`}</p>
//                 <p className="text-gray-400 text-[10px] sm:text-xs mt-1">{payload[0].value >= 15 ? 'Peak activity period' : 'Normal activity'}</p>
//             </div>
//         );
//     };

//     return (
//         <div className="bg-transparent border border-[var(--border-color2)] rounded-lg p-4 sm:p-6 relative">
//             {/* Header + Controls */}
//             <div className="flex flex-col sm:flex-row sm:items-start sm:justify-between gap-4 sm:gap-6 mb-4">
//                 <div className="min-w-0">
//                     <h3 className="text-base sm:text-lg md:text-xl font-semibold text-black">Engagement Timeline</h3>
//                     <p className="text-xs sm:text-sm text-black">
//                         Tap activity over time • {timeRange.charAt(0).toUpperCase() + timeRange.slice(1)} view
//                     </p>
//                 </div>

//                 <div className="flex flex-col sm:flex-row sm:items-center gap-3 sm:gap-4">
//                     <label className="flex items-center space-x-2">
//                         <input
//                             type="checkbox"
//                             checked={isDateFilterEnabled}
//                             onChange={() => setIsDateFilterEnabled(!isDateFilterEnabled)}
//                             className="checkbox"
//                         />
//                         <span className="text-sm">Enable Date/Time Filter</span>
//                     </label>

//                     {isDateFilterEnabled && (
//                         <div className="flex items-center gap-2 flex-wrap sm:flex-nowrap overflow-x-auto">
//                             <DatePicker
//                                 selected={startDate}
//                                 onChange={(date) => setStartDate(date)}
//                                 showTimeSelect
//                                 dateFormat="Pp"
//                                 wrapperClassName="shrink-0"
//                                 className="w-[220px] shrink-0 whitespace-nowrap overflow-hidden text-ellipsis text-sm px-3 py-2 rounded-lg border border-gray-600"
//                                 placeholderText="Start Date"
//                             />
//                             <DatePicker
//                                 selected={endDate}
//                                 onChange={(date) => setEndDate(date)}
//                                 showTimeSelect
//                                 dateFormat="Pp"
//                                 wrapperClassName="shrink-0"
//                                 className="w-[220px] shrink-0 whitespace-nowrap overflow-hidden text-ellipsis text-sm px-3 py-2 rounded-lg border border-gray-600"
//                                 placeholderText="End Date"
//                             />
//                         </div>
//                     )}

//                     <TimeRangeSelector />
//                 </div>
//             </div>

//             {/* States */}
//             {loading && (
//                 <div className="h-56 sm:h-64 flex items-center justify-center text-gray-400">
//                     <div className="loader" />
//                 </div>
//             )}

//             {error && !loading && (
//                 <div className="h-56 sm:h-64 flex items-center justify-center text-red-400 text-sm sm:text-base">
//                     {error}
//                 </div>
//             )}

//             {isDataEmpty && (
//                 <NoDataFound
//                     title="No Engagement Recorded"
//                     icon={Clock}
//                     message={`We haven't recorded any taps for this event in the selected date range. Try changing the date range!`}
//                     height="h-56 sm:h-64"
//                 />
//             )}

//             {/* Chart */}
//             {!loading && !error && filteredData.length > 0 && (
//                 <div className="h-56 sm:h-64 md:h-80 w-full min-w-0">
//                     <ResponsiveContainer width="100%" height="100%">
//                         <LineChart data={filteredData} margin={{ top: 5, right: 16, left: 8, bottom: 8 }}>
//                             <CartesianGrid strokeDasharray="1 1" stroke="#333333" />
//                             <XAxis
//                                 dataKey="date"
//                                 tick={{ fill: '#9ca3af', fontSize: 10 }}
//                                 tickMargin={8}
//                                 interval="preserveEnd"
//                             />
//                             <YAxis tick={{ fill: '#9ca3af', fontSize: 10 }} tickMargin={6} />
//                             <Tooltip content={<CustomTooltip />} />
//                             <Line type="monotone" dataKey="rate" stroke="#ff6b35" strokeWidth={2} dot={false} />
//                         </LineChart>
//                     </ResponsiveContainer>
//                 </div>
//             )}
//         </div>
//     );
// };

// export default EngagementChart;
import useClickTimeline from "../../../hooks/useClickTimeline";
import useNormalizedTimeline from "../../../hooks/useNormalizedTimeline";
import React, { useMemo, useRef, useState } from 'react';
import { ChevronDown, Clock, Globe } from 'lucide-react';
import { LineChart, Line, XAxis, YAxis, CartesianGrid, ResponsiveContainer, Tooltip } from 'recharts';
import { useClickOutside } from "../../../hooks/ClickoutsideHook";
import NoDataFound from "../dataDisplay/NoDataFound";
import DatePicker from 'react-datepicker';
import "react-datepicker/dist/react-datepicker.css";

/**
 * Displays tap activity data over time (hourly, daily, or monthly) in a line chart.
 *
 * @param {object} props - The component props.
 * @param {string} props.eventId - The unique ID of the event to fetch analytics for.
 * @param {string} props.token - The user authentication token (passed to the hook for dependency).
 * @returns {React.ReactElement} The Engagement Timeline chart component.
 */
const EngagementChart = ({ eventId, token }) => {
    const { data: raw, loading, error } = useClickTimeline({ eventId, token });
    const { hourly, daily, monthly, minutes } = useNormalizedTimeline(raw);

    const [timeRange, setTimeRange] = useState('daily');
    const [startDate, setStartDate] = useState(new Date());
    const [endDate, setEndDate] = useState(new Date());
    const [isDateFilterEnabled, setIsDateFilterEnabled] = useState(false);
    const [selectedTimezone, setSelectedTimezone] = useState('America/New_York'); // Default EST

    // Timezone options
    const timezoneOptions = [
        { value: 'America/New_York', label: 'EST - Eastern Time' },
        { value: 'America/Chicago', label: 'CST - Central Time' },
        { value: 'America/Denver', label: 'MST - Mountain Time' },
        { value: 'America/Los_Angeles', label: 'PST - Pacific Time' },
        { value: 'America/Anchorage', label: 'AKST - Alaska Time' },
        { value: 'Pacific/Honolulu', label: 'HST - Hawaii Time' },
        { value: 'UTC', label: 'UTC - Universal Time' },
    ];

    // Format date based on timezone and time range
    const formatDateForTimezone = (dateString, timezone, range) => {
        // Parse the date string - handles both ISO strings and custom formats
        let date;

        // If it's already in "YYYY-MM-DD HH:MM AM/PM" format from API
        if (typeof dateString === 'string' && dateString.includes(' ')) {
            // Convert to ISO format for proper parsing
            const parts = dateString.match(/(\d{4})-(\d{2})-(\d{2})\s+(\d{1,2}):(\d{2})\s+(AM|PM)/);
            if (parts) {
                let [, year, month, day, hour, minute, meridiem] = parts;
                hour = parseInt(hour);
                if (meridiem === 'PM' && hour !== 12) hour += 12;
                if (meridiem === 'AM' && hour === 12) hour = 0;
                date = new Date(Date.UTC(year, month - 1, day, hour, minute));
            } else {
                date = new Date(dateString);
            }
        } else {
            // Standard ISO string or date object
            date = new Date(dateString);
        }

        const options = {
            timeZone: timezone,
        };

        switch (range) {
            case 'minutes':
                return date.toLocaleString('en-US', {
                    ...options,
                    month: 'short',
                    day: 'numeric',
                    hour: 'numeric',
                    minute: '2-digit',
                    hour12: true
                });
            case 'hourly':
                return date.toLocaleString('en-US', {
                    ...options,
                    month: 'short',
                    day: 'numeric',
                    hour: 'numeric',
                    hour12: true
                });
            case 'monthly':
                return date.toLocaleString('en-US', {
                    ...options,
                    year: 'numeric',
                    month: 'short'
                });
            default: // daily
                return date.toLocaleString('en-US', {
                    ...options,
                    month: 'short',
                    day: 'numeric',
                    year: 'numeric'
                });
        }
    };

    // Get timezone abbreviation for display
    const getTimezoneAbbr = (timezone) => {
        const date = new Date();
        const formatter = new Intl.DateTimeFormat('en-US', {
            timeZone: timezone,
            timeZoneName: 'short'
        });
        const parts = formatter.formatToParts(date);
        const tzPart = parts.find(part => part.type === 'timeZoneName');
        return tzPart ? tzPart.value : '';
    };

    // Transform data with timezone-aware formatting
    const data = useMemo(() => {
        let rawData;
        switch (timeRange) {
            case 'hourly': rawData = hourly; break;
            case 'monthly': rawData = monthly; break;
            case 'minutes': rawData = minutes; break;
            default: rawData = daily;
        }

        // Format dates according to selected timezone
        return rawData.map(item => {
            const displayDate = formatDateForTimezone(item.date, selectedTimezone, timeRange);

            return {
                ...item,
                displayDate,
                originalDate: item.date,
                // Store the actual converted date object for accurate filtering
                convertedDate: new Date(new Date(item.date).toLocaleString('en-US', {
                    timeZone: selectedTimezone
                }))
            };
        });
    }, [timeRange, hourly, daily, monthly, minutes, selectedTimezone]);

    // Filter the data if the date filter is enabled
    const filteredData = useMemo(() => {
        if (!isDateFilterEnabled) return data;
        return data.filter(item => {
            const itemDate = new Date(item.originalDate);
            return itemDate >= startDate && itemDate <= endDate;
        });
    }, [data, isDateFilterEnabled, startDate, endDate]);

    const isDataEmpty = !loading && !error && filteredData.length === 0;

    const TimeRangeSelector = () => {
        const [isOpen, setIsOpen] = useState(false);
        const ref = useRef(null);
        useClickOutside(ref, () => setIsOpen(false));

        const options = [
            { value: 'minutes', label: 'Minutes Data' },
            { value: 'hourly', label: 'Hourly Data' },
            { value: 'daily', label: 'Daily Data' },
            { value: 'monthly', label: 'Monthly Data' },
        ];
        const currentOption = options.find((opt) => opt.value === timeRange);

        return (
            <div className="relative w-full sm:w-auto" ref={ref}>
                <button
                    onClick={() => setIsOpen(!isOpen)}
                    className="w-full sm:w-auto bg-[var(--analytics-card-color)] text-black px-3 py-2 rounded-lg flex items-center justify-between sm:justify-center space-x-2 transition-colors border border-gray-600 cursor-pointer"
                >
                    <span className="text-sm sm:text-xs md:text-sm font-medium">{currentOption?.label}</span>
                    <ChevronDown className={`w-4 h-4 transition-transform ${isOpen ? 'rotate-180' : ''}`} />
                </button>
                {isOpen && (
                    <div className="absolute top-full mt-2 right-0 left-0 sm:left-auto bg-[var(--analytics-card-color)] border border-[var(--border-color)] rounded-lg shadow-lg z-10 min-w-[160px]">
                        {options.map((option) => (
                            <button
                                key={option.value}
                                onClick={() => { setTimeRange(option.value); setIsOpen(false); }}
                                className={`w-full text-left px-4 py-2 text-sm hover:bg-gray-300 transition-colors first:rounded-t-lg last:rounded-b-lg ${timeRange === option.value ? 'bg-gray-300 text-orange-400' : 'text-black'}`}
                            >
                                {option.label}
                            </button>
                        ))}
                    </div>
                )}
            </div>
        );
    };

    const TimezoneSelector = () => {
        const [isOpen, setIsOpen] = useState(false);
        const ref = useRef(null);
        useClickOutside(ref, () => setIsOpen(false));

        const currentOption = timezoneOptions.find((opt) => opt.value === selectedTimezone);

        return (
            <div className="relative w-full sm:w-auto" ref={ref}>
                <button
                    onClick={() => setIsOpen(!isOpen)}
                    className="w-full sm:w-auto bg-[var(--analytics-card-color)] text-black px-3 py-2 rounded-lg flex items-center justify-between sm:justify-center space-x-2 transition-colors border border-gray-600 cursor-pointer"
                >
                    <Globe className="w-4 h-4" />
                    <span className="text-sm sm:text-xs md:text-sm font-medium">{currentOption?.label}</span>
                    <ChevronDown className={`w-4 h-4 transition-transform ${isOpen ? 'rotate-180' : ''}`} />
                </button>
                {isOpen && (
                    <div className="absolute top-full mt-2 right-0 left-0 sm:left-auto bg-[var(--analytics-card-color)] border border-[var(--border-color)] rounded-lg shadow-lg z-10 min-w-[200px]">
                        {timezoneOptions.map((option) => (
                            <button
                                key={option.value}
                                onClick={() => { setSelectedTimezone(option.value); setIsOpen(false); }}
                                className={`w-full text-left px-4 py-2 text-sm hover:bg-gray-300 transition-colors first:rounded-t-lg last:rounded-b-lg ${selectedTimezone === option.value ? 'bg-gray-300 text-orange-400' : 'text-black'}`}
                            >
                                {option.label}
                            </button>
                        ))}
                    </div>
                )}
            </div>
        );
    };

    const CustomTooltip = ({ active, payload, label }) => {
        if (!active || !payload || !payload.length) return null;

        const getTooltipLabel = () =>
            timeRange === 'hourly' ? 'Time' :
                timeRange === 'monthly' ? 'Month' :
                    timeRange === 'minutes' ? 'Time' : 'Date';

        const getUnit = () =>
            timeRange === 'hourly' ? 'taps/hour' :
                timeRange === 'monthly' ? 'taps/month' :
                    timeRange === 'daily' ? 'taps/day' : 'taps/minute';

        return (
            <div className="bg-[var(--analytics-card-color)] border border-[var(--border-color)] rounded-lg p-3 shadow-lg">
                <p className="text-black text-xs sm:text-sm font-medium">{`${getTooltipLabel()}: ${label}`}</p>
                <p className="text-orange-400 text-xs sm:text-sm">{`Engagement: ${payload[0].value} ${getUnit()}`}</p>
                <p className="text-gray-400 text-[10px] sm:text-xs mt-1">{payload[0].value >= 15 ? 'Peak activity period' : 'Normal activity'}</p>
            </div>
        );
    };

    return (
        <div className="bg-transparent border border-[var(--border-color2)] rounded-lg p-4 sm:p-6 relative">
            {/* Header + Controls */}
            <div className="flex flex-col sm:flex-row sm:items-start sm:justify-between gap-4 sm:gap-6 mb-4">
                <div className="min-w-0">
                    <h3 className="text-base sm:text-lg md:text-xl font-semibold text-black">Engagement Timeline</h3>
                    <p className="text-xs sm:text-sm text-black">
                        Tap activity over time • {timeRange.charAt(0).toUpperCase() + timeRange.slice(1)} view • {getTimezoneAbbr(selectedTimezone)}
                    </p>
                </div>
                <div className="flex flex-col sm:flex-row sm-justify-between items-center gap-3">
                    {/* Controls Row 1: Date Filter Checkbox */}
                    <div className="flex gap-2 flex-col sm:flex-row ">
                        <div className="flex ">
                            <label className="flex  space-x-2 items-center ">
                                <input
                                    type="checkbox"
                                    checked={isDateFilterEnabled}
                                    onChange={() => setIsDateFilterEnabled(!isDateFilterEnabled)}
                                    className="checkbox"
                                />
                                <span className="text-sm">Enable Date/Time Filter</span>
                            </label>
                        </div>

                        {/* Controls Row 2: Date Pickers (if enabled) */}
                        {isDateFilterEnabled && (
                            <div className="flex gap-2 flex-wrap">
                                <DatePicker
                                    selected={startDate}
                                    onChange={(date) => setStartDate(date)}
                                    showTimeSelect
                                    dateFormat="Pp"
                                    wrapperClassName="shrink-0"
                                    className="w-[220px] shrink-0 whitespace-nowrap overflow-hidden text-ellipsis text-sm px-3 py-2 rounded-lg border border-gray-600"
                                    placeholderText="Start Date"
                                />
                                <DatePicker
                                    selected={endDate}
                                    onChange={(date) => setEndDate(date)}
                                    showTimeSelect
                                    dateFormat="Pp"
                                    wrapperClassName="shrink-0"
                                    className="w-[220px] shrink-0 whitespace-nowrap overflow-hidden text-ellipsis text-sm px-3 py-2 rounded-lg border border-gray-600"
                                    placeholderText="End Date"
                                />
                            </div>
                        )}
                    </div>

                    {/* Controls Row 3: Time Range & Timezone Selectors */}
                    <div className="flex flex-wrap gap-3">
                        <TimeRangeSelector />
                        <TimezoneSelector />
                    </div>
                </div>
            </div>

            {/* States */}
            {loading && (
                <div className="h-56 sm:h-64 flex items-center justify-center text-gray-400">
                    <div className="loader" />
                </div>
            )}

            {error && !loading && (
                <div className="h-56 sm:h-64 flex items-center justify-center text-red-400 text-sm sm:text-base">
                    {error}
                </div>
            )}

            {isDataEmpty && (
                <NoDataFound
                    title="No Engagement Recorded"
                    icon={Clock}
                    message={`We haven't recorded any taps for this event in the selected date range. Try changing the date range!`}
                    height="h-56 sm:h-64"
                />
            )}

            {/* Chart */}
            {!loading && !error && filteredData.length > 0 && (
                <div className="h-56 sm:h-64 md:h-80 w-full min-w-0">
                    <ResponsiveContainer width="100%" height="100%">
                        <LineChart data={filteredData} margin={{ top: 5, right: 16, left: 8, bottom: 8 }}>
                            <CartesianGrid strokeDasharray="1 1" stroke="#333333" />
                            <XAxis
                                dataKey="displayDate"
                                tick={{ fill: '#9ca3af', fontSize: 10 }}
                                tickMargin={8}
                                interval="preserveEnd"
                            />
                            <YAxis tick={{ fill: '#9ca3af', fontSize: 10 }} tickMargin={6} />
                            <Tooltip content={<CustomTooltip />} />
                            <Line type="monotone" dataKey="rate" stroke="#ff6b35" strokeWidth={2} dot={false} />
                        </LineChart>
                    </ResponsiveContainer>
                </div>
            )}
        </div>
    );
};

export default EngagementChart;